<script setup lang="ts">
import { computed } from 'vue'
import { dateFormat } from '@/components/helpers/formats'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import type { DonorDonation } from '@/types/donorFinancialYear'
import {
  donationSourcePillClass,
  isManualDonationSource,
} from '@/helpers/donationSource'
import { useDonationDetailModal } from '@/composables/useDonationDetailModal'
import CharityAvatar from '@/components/dashboard/ui/CharityAvatar.vue'
import ClaimedDonationMark from '@/components/dashboard/ui/ClaimedDonationMark.vue'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'
import { isDonationIncludedInClaim } from '@/helpers/claimTaxBack/includedInClaim'

const props = withDefaults(
  defineProps<{
    donation: DonorDonation
    /** Show notes under the date when present on the donation. */
    showNotes?: boolean
    /**
     * `occurrence` is the compact Donations-list row used inside a recurring
     * set, where the charity is already known from the parent view.
     */
    variant?: 'default' | 'summary' | 'occurrence'
    summaryLabel?: string
    summaryGiftAidPence?: number
    /** When false, emit `select` instead of opening the donation detail modal. */
    openOnClick?: boolean
    /**
     * Hide the source pill when the surrounding tab already makes the source
     * obvious, for example Added by you.
     */
    showSource?: boolean
  }>(),
  {
    showNotes: false,
    variant: 'default',
    openOnClick: true,
    showSource: true,
  }
)

const emit = defineEmits<{
  select: []
}>()

const { openDonationDetailModal } = useDonationDetailModal()
const { profile } = useClaimTaxBack()

const isManual = computed(() => isManualDonationSource(props.donation.source))

const isSummary = computed(() => props.variant === 'summary')
const isOccurrence = computed(() => props.variant === 'occurrence')

const isInteractive = computed(() => !isSummary.value)

const showIdentity = computed(() => !isSummary.value && !isOccurrence.value)

const showSourcePill = computed(
  () => showIdentity.value && isManual.value && props.showSource
)

const charityLabel = computed(() => {
  if (isOccurrence.value) return dateFormat(props.donation.time)
  if (isSummary.value) return props.summaryLabel ?? props.donation.charity
  return props.donation.charity
})

const sourcePillClass = computed(() => donationSourcePillClass(props.donation))

const showGiftAid = computed(() => props.donation.giftaid > 0)

const includedInClaim = computed(
  () =>
    !isSummary.value &&
    !isOccurrence.value &&
    isDonationIncludedInClaim(props.donation, profile.value)
)

const showSummaryGiftAid = computed(
  () => isSummary.value && (props.summaryGiftAidPence ?? 0) > 0
)

const showGiftAidStatus = computed(() => !isSummary.value || showSummaryGiftAid.value)

const noteText = computed(() => props.donation.notes?.trim() ?? '')

const openDetail = () => {
  if (!isInteractive.value) return
  if (!props.openOnClick) {
    emit('select')
    return
  }
  openDonationDetailModal(props.donation)
}
</script>

<template>
  <component
    :is="isInteractive ? 'button' : 'div'"
    class="sa-donation-row"
    :class="{
      'sa-donation-row--summary': isSummary,
      'sa-donation-row--occurrence': isOccurrence,
      'sa-donation-row--interactive': isInteractive,
    }"
    :type="isInteractive ? 'button' : undefined"
    role="listitem"
    @click="openDetail"
  >
    <CharityAvatar
      v-if="showIdentity"
      :charity="donation.charity"
      :logo-url="donation.charityLogoUrl"
      :brand-color="donation.charityBrandColor"
    />
    <div class="sa-donation-main">
      <div class="sa-donation-charity">{{ charityLabel }}</div>
      <div v-if="showIdentity" class="sa-donation-date">
        {{ dateFormat(donation.time) }}
      </div>
      <div v-if="showNotes && noteText" class="sa-donation-notes">{{ noteText }}</div>
    </div>
    <!-- Desktop only: a fixed source column so Gift Aid copy cannot shift it. -->
    <div v-if="showIdentity" class="sa-donation-source-col">
      <span
        v-if="showSourcePill"
        class="source-pill source-pill--row-end"
        :class="sourcePillClass"
      >
        {{ $t('Dashboard.Home.AddedByYou') }}
      </span>
    </div>
    <div class="sa-donation-right">
      <div class="sa-donation-amount">{{ formatMoneyFromPence(donation.amount) }}</div>
      <div v-if="showGiftAid" class="sa-donation-ga">
        +{{ formatMoneyFromPence(donation.giftaid) }} Gift Aid
      </div>
      <div v-else-if="showSummaryGiftAid" class="sa-donation-ga">
        +{{ formatMoneyFromPence(summaryGiftAidPence ?? 0) }} Gift Aid
      </div>
      <div v-else-if="showGiftAidStatus" class="sa-donation-ga sa-donation-ga--none">
        {{ $t('Dashboard.Giving.Table.NoGiftAid') }}
      </div>
      <ClaimedDonationMark v-if="includedInClaim" />
      <!-- Mobile only: source closes the right-hand stack under the Gift Aid state. -->
      <span
        v-if="showSourcePill"
        class="source-pill source-pill--row-end sa-donation-inline-source"
        :class="sourcePillClass"
      >
        {{ $t('Dashboard.Home.AddedByYou') }}
      </span>
    </div>
  </component>
</template>
